import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBBadge from 'components/PMBBadge'
import PMBTooltip from 'components/PMBTooltip'
import { capitalizeEveryWord } from 'helpers'
import moment from 'moment'
import { Calendar, Target, Users, Edit2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Badge, Col, Nav, NavItem, NavLink, Progress, Row } from 'reactstrap'
import { formatDateOnlyPMB } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_PROJECTS_MENU } from 'utils/pathUrl'
import StatCard from './components/StatCard'
import DeliveryTimeline from './DeliveryTimeline'
import InventoryAnalytics from './InventoryAnalytics'
import ModalEditCurrentAmount from './ModalEditCurrentAmount'
import {
  useFetchProjectSummaryDashboardQuery,
  useFetchProjectSummarySharedDashboardQuery,
} from './projectDashboardListApiSlice'
import { useListResourcesAccessQuery } from '../projects-menu/projectsMenuListApiSlice'
import { setActiveTab } from './projectDashboardListSlice'
import './styles.css'

const ProjectDashboard = ({
  projectId: propProjectId,
  embedded = false,
  isShared = false,
}) => {
  const dispatch = useDispatch()
  const { tag, activeTab } = useSelector((state) => state.projectDashboardList)
  const { profile } = useSelector((state) => state.dashboard)
  const { projectId: locationProjectId } = useLocation().state || {}
  const [selectedGoal, setSelectedGoal] = useState(null)
  const [isOpenModalEdit, setIsOpenModalEdit] = useState(false)

  const projectId = locationProjectId || propProjectId

  useEffect(() => {
    if (isShared && activeTab === 'inventory') {
      dispatch(setActiveTab('delivery'))
    }
  }, [isShared, activeTab, dispatch])

  const { data: resourceAccessData } = useListResourcesAccessQuery(
    { resourceId: projectId, resourceType: 'PROJECT' },
    { skip: !isShared || !projectId },
  )

  const {
    data: ownData,
    isLoading: isLoadingOwn,
    refetch: refetchOwn,
    isFetching: isFetchingOwn,
  } = useFetchProjectSummaryDashboardQuery(
    { projectId },
    { refetchOnMountOrArgChange: true, skip: isShared },
  )

  const {
    data: sharedData,
    isLoading: isLoadingShared,
    refetch: refetchShared,
    isFetching: isFetchingShared,
  } = useFetchProjectSummarySharedDashboardQuery(
    { projectId },
    { refetchOnMountOrArgChange: true, skip: !isShared },
  )

  const data = isShared ? sharedData : ownData
  const isLoading = isShared ? isLoadingShared : isLoadingOwn
  const isFetching = isShared ? isFetchingShared : isFetchingOwn
  const refetch = isShared ? refetchShared : refetchOwn

  if (isLoading || isFetching) {
    return <LoadingV2 />
  }

  const apiData = data || {}

  // CHECK USER ROLE FOR SHARED PROJECT
  const getUserRole = () => {
    if (!isShared || !profile?.userId || !resourceAccessData?.participants)
      return null
    const participant = resourceAccessData.participants.find(
      (p) => p.userId === profile.userId,
    )
    return participant?.role || null
  }

  const userRole = getUserRole()
  const canEditProject =
    !isShared || userRole === 'OWNER' || userRole === 'EDITOR'

  // CALCULATE DAYS REMAINING
  const calculateDaysRemaining = (endDate) => {
    const end = moment(endDate)
    const today = moment()
    return end.diff(today, 'days')
  }

  // GET INITIALS FROM NAME
  // const getInitials = (name) => {
  //   return name
  //     ?.split(' ')
  //     ?.map((word) => word[0])
  //     ?.join('')
  //     ?.substring(0, 2)
  //     ?.toUpperCase()
  // }

  const progressPercentage = apiData.pctgVsGoal || 0
  const daysRemaining = apiData.endDate
    ? calculateDaysRemaining(apiData.endDate)
    : '0'

  // PREPARE PROJECT DATA
  const dataParticipants =
    apiData?.participants?.filter(
      (participant) => participant.role !== 'OWNER',
    ) || []

  const projectData = {
    title: apiData.title || 'Project',
    label: apiData.label || 'Project',
    description: apiData.description || '',
    goals: apiData.goals || [],
    timeline: {
      daysRemaining: daysRemaining,
      startDate: apiData.startDate ? formatDateOnlyPMB(apiData.startDate) : '',
      endDate: apiData.endDate ? formatDateOnlyPMB(apiData.endDate) : '',
    },
    team: {
      participants: dataParticipants?.length || 0,
      members:
        dataParticipants
          ?.slice(0, 3)
          ?.map(
            (p) =>
              p?.verifiedprofile?.partnership?.name || p?.verifiedprofile?.name,
          ) || [],
    },
  }

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab))
  }

  const remainingParticipants = dataParticipants?.slice(3) || []
  const remainingParticipantNames = remainingParticipants
    ?.map(
      (p) => p?.verifiedprofile?.partnership?.name || p?.verifiedprofile?.name,
    )
    ?.filter(Boolean)
    ?.join(', ')

  return (
    <div className={embedded ? 'w-100' : 'w-100 px-3'}>
      <ModalEditCurrentAmount
        isOpen={isOpenModalEdit}
        toggle={() => setIsOpenModalEdit(!isOpenModalEdit)}
        goal={selectedGoal}
        refetch={refetch}
        isShared={isShared}
      />

      {!embedded && (
        <>
          <div className="mt-3">
            <BreadCrumbs
              breadCrumbActive={`${projectData?.label}`}
              breadCrumbParent={'Daftar Projects'}
              breadCrumbParentLink={URL_PROJECTS_MENU}
            />
          </div>

          {/* HEADER SECTION */}
          <Row className="my-3">
            <Col xs="12">
              <div className="d-flex flex-column mb-4">
                <h4 className="mb-2 fw-bold">{projectData.title}</h4>
                <p className="text-muted mb-0">{projectData.label}</p>
              </div>
            </Col>
          </Row>
        </>
      )}

      {/* STATS CARDS */}
      <Row className="g-3 mb-4">
        {/* TIMELINE CARD */}
        <Col xs="12" md="6">
          <StatCard
            icon={<Calendar size={20} style={{ color: '#DC6803' }} />}
            iconBgColor="#FFFCF5"
            title="Timeline"
            value={
              <div className="fw-semibold mb-1">
                {projectData.timeline.daysRemaining} days remaining
              </div>
            }
            subtitle={` ${projectData.timeline.startDate} - ${projectData.timeline.endDate}`}
          />
        </Col>

        {/* TEAM CARD */}
        <Col xs="12" md="6">
          <StatCard
            icon={<Users size={20} style={{ color: '#039855' }} />}
            iconBgColor="#F6FEF9"
            title="Team"
            value={
              <div className="fw-semibold mb-2">
                {projectData.team.participants} participants
              </div>
            }
          >
            <div className="d-flex gap-1 align-items-center">
              {projectData.team.members.map((member, index) => (
                <PMBBadge
                  key={index}
                  title={member}
                  className="px-2 w-auto"
                  size="small"
                />
              ))}
              {projectData.team.participants >
                projectData.team.members.length && (
                <>
                  <Badge
                    id="remaining-participants-tooltip"
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '6px',
                    }}
                  >
                    +
                    {projectData.team.participants -
                      projectData.team.members.length}
                  </Badge>
                  <PMBTooltip
                    target="remaining-participants-tooltip"
                    placement="top"
                    text={remainingParticipantNames || 'Other participants'}
                  />
                </>
              )}
            </div>
          </StatCard>
        </Col>
      </Row>

      {/* PROJECT GOALS SECTION */}
      {projectData.goals && projectData.goals.length > 0 && (
        <>
          <Row className="mb-3">
            <Col xs="12">
              <h6 className="mb-0 fw-semibold">Project Goals</h6>
            </Col>
          </Row>
          <div className="mb-4">
            <div
              className="d-flex gap-3 pb-3 goals-scroll-container"
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {projectData.goals.map((goal, index) => {
                // CALCULATE PROGRESS FOR THIS SPECIFIC GOAL
                const goalItem = apiData.items?.find(
                  (item) => item.itemId === goal.itemId,
                )
                const currentAmount = goalItem?.totalInventory || 0
                const targetAmount = goal.amount || 0
                const goalProgress =
                  targetAmount > 0
                    ? Math.round((goal?.currentAmount / targetAmount) * 100)
                    : 0

                return (
                  <div
                    key={index}
                    style={{
                      minWidth: '280px',
                      maxWidth: '280px',
                      flexShrink: 0,
                    }}
                    data-testid={`project-dashboard-goal-card-${goal?.itemId}`}
                  >
                    <StatCard
                      icon={<Target size={20} style={{ color: '#006386' }} />}
                      iconBgColor="#ECF9FE"
                      title={goal.nameItem}
                      value={
                        <div className="fw-semibold mb-1">
                          {newFormatThousandSeparator(currentAmount)} /{' '}
                          {newFormatThousandSeparator(targetAmount)}{' '}
                          {capitalizeEveryWord(goal.unit)}
                        </div>
                      }
                    >
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <div>
                          Current amount:{' '}
                          {newFormatThousandSeparator(goal?.currentAmount)}{' '}
                          {capitalizeEveryWord(goal.unit)}
                        </div>
                        {apiData?.status !== 'CLOSED' && canEditProject && (
                          <div
                            style={{ cursor: 'pointer', padding: '4px' }}
                            onClick={() => {
                              setSelectedGoal(goal)
                              setIsOpenModalEdit(true)
                            }}
                            data-testid={`project-dashboard-goal-edit-${goal?.itemId}`}
                          >
                            <Edit2 size={14} color="#006386" />
                          </div>
                        )}
                      </div>
                      <Progress
                        value={goalProgress}
                        className="mb-2 mt-3"
                        style={{ height: '8px' }}
                        color={
                          goalProgress >= 100
                            ? 'success'
                            : goalProgress >= 50
                              ? 'primary'
                              : 'warning'
                        }
                      />
                      <div className="text-muted small">
                        {goalProgress}% complete
                      </div>
                    </StatCard>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* TABS NAVIGATION */}
      <Row className="mb-3">
        <Col xs="12">
          <Nav tabs className="border-bottom">
            {isShared ? null : (
              <NavItem>
                <NavLink
                  className={`cursor-pointer ${
                    activeTab === 'inventory' ? 'active' : ''
                  }`}
                  onClick={() => handleTabChange('inventory')}
                  style={{
                    cursor: 'pointer',
                    borderBottom:
                      activeTab === 'inventory' ? '2px solid #006386' : 'none',
                    color: activeTab === 'inventory' ? '#006386' : '#667085',
                    fontWeight: activeTab === 'inventory' ? '600' : '400',
                  }}
                >
                  Inventory Analytics
                </NavLink>
              </NavItem>
            )}
            <NavItem>
              <NavLink
                className={`cursor-pointer ${
                  activeTab === 'delivery' ? 'active' : ''
                }`}
                onClick={() => handleTabChange('delivery')}
                style={{
                  cursor: 'pointer',
                  borderBottom:
                    activeTab === 'delivery' ? '2px solid #006386' : 'none',
                  color: activeTab === 'delivery' ? '#006386' : '#667085',
                  fontWeight: activeTab === 'delivery' ? '600' : '400',
                }}
              >
                Delivery Timeline
              </NavLink>
            </NavItem>
          </Nav>
        </Col>
      </Row>

      {/* TAB CONTENT */}
      <Row>
        <Col xs="12">
          {activeTab === 'inventory' && !isShared && (
            <InventoryAnalytics items={apiData.items || []} />
          )}
          {activeTab === 'delivery' && (
            <DeliveryTimeline
              deliveryTimeLines={apiData.deliveryTimeLines || []}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

export default ProjectDashboard
